import { tokens, tsr } from '../../lib/api.js';
import { orgSubdomainOrigin } from '../../lib/subdomain.js';

/** Shared by the switch menu, the profile menu and the Group access page. */
export const COMPANY_GROUP_QUERY_KEY = ['company-group'] as const;

/**
 * The caller's company group (docs/adr/0034-company-groups.md): its companies, whether they own
 * it, and whether they may create another company. Cheap and rarely changing, so cached a while.
 */
export function useCompanyGroup() {
  const query = tsr.companyGroup.getMyGroup.useQuery({
    queryKey: COMPANY_GROUP_QUERY_KEY,
    staleTime: 5 * 60_000,
  });
  const data = query.data?.status === 200 ? query.data.body : null;
  return {
    group: data?.group ?? null,
    isOwner: data?.isOwner ?? false,
    canCreateCompany: data?.canCreateCompany ?? false,
    isLoading: query.isLoading,
  };
}

/**
 * Lands the browser on another company's own subdomain, already signed in, from a one-time handoff
 * code — the same mechanics as the switch menu and Login.tsx: a session lives in that origin's
 * storage, so the code crosses the redirect and is redeemed there. `localhost` has no real
 * subdomains, so there the code is redeemed in place and the page reloads. `next` is a path on that
 * company to land on instead of its home page (SessionHandoff only follows paths on its own origin).
 */
export async function goToCompany(slug: string, code: string, next?: string): Promise<void> {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    const redeemed = await tsr.auth.redeemSessionHandoff.mutate({ body: { code } });
    if (redeemed.status === 200) await tokens.save(redeemed.body.tokens);
    window.location.assign(next ?? '/');
    return;
  }
  window.location.href = `${orgSubdomainOrigin(slug)}/session-handoff?code=${encodeURIComponent(code)}${next ? `&next=${encodeURIComponent(next)}` : ''}`;
}
